import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import PageShell from "@/components/site/PageShell";
import PageHeader from "@/components/site/PageHeader";
import { Container, Eyebrow, H2, Body, ArrowLink, Reveal } from "@/components/site/ui";
import { DOCENCIA, DISCIPLINA, LENTES_HUMANAS, IMG } from "@/content/site";
import { MapaAprendizagem } from "@/components/site/MapaAprendizagem";
import { QuestoesTransversais } from "@/components/site/QuestoesTransversais";

const Abordagem = () => (
    <section data-section-id="abordagem" className="py-16 md:py-24">
        <Container className="grid grid-cols-1 lg:grid-cols-2 gap-14">
            <Reveal>
                <Eyebrow>Abordagem</Eyebrow>
                <ul className="flex flex-col gap-5">
                    {DOCENCIA.principios.map((p) => (
                        <li key={p} className="font-serif font-light text-[22px] md:text-[26px] leading-[1.35] text-[#F2EFE5]">{p}</li>
                    ))}
                </ul>
            </Reveal>
            <Reveal delay={120}>
                <Eyebrow>{DOCENCIA.autoria.titulo}</Eyebrow>
                <Body>{DOCENCIA.autoria.lead}</Body>
                <ul data-testid="docencia-autoria" className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                    {DOCENCIA.autoria.itens.map((t) => (
                        <li key={t} className="py-3.5 border-t border-[#1B3025] font-sans font-light text-[14.5px] text-[#D7D1BF]">{t}</li>
                    ))}
                </ul>
                <p className="mt-10 font-sans text-[10px] uppercase tracking-[0.28em] text-[#7E8978]">Temas de ensino</p>
                <p className="mt-3 font-serif italic text-[16px] leading-[1.7] text-[#D7D1BF]">{DOCENCIA.temas.join(" · ")}</p>
            </Reveal>
        </Container>
    </section>
);

const Disciplina = () => {
    const ind = LENTES_HUMANAS.find((l) => l.k === "Individuação");
    return (
        <section id="disciplina" data-section-id="disciplina" data-testid="docencia-disciplina" className="relative bg-[#F2EFE5] text-[#0E2018] py-20 md:py-28 scroll-mt-20">
            <div aria-hidden="true" className="absolute inset-0 paper-grain" />
            <Container className="relative">
                <Reveal className="max-w-4xl">
                    <p className="inline-flex items-center gap-2 font-sans text-[10px] uppercase tracking-[0.26em] text-[#8a6a22] border border-[#8a6a22]/50 rounded-full px-3 py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#8a6a22]" /> {DISCIPLINA.status}
                    </p>
                    <h2 className="mt-8 font-serif font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.015em] text-balance">{DISCIPLINA.nome}</h2>
                    <p className="mt-4 font-serif italic text-[20px] md:text-[24px] text-[#8a6a22]">{DISCIPLINA.subtitulo}</p>
                </Reveal>
                <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-14">
                    <Reveal delay={100} className="flex flex-col gap-6">
                        <Body light>{DISCIPLINA.proposta}</Body>
                        <Body light>{DISCIPLINA.proposta2}</Body>
                        <p data-testid="disciplina-adaptacao" className="font-sans font-light text-[13.5px] leading-[1.75] text-[#5b6658] border-l border-[#0E2018]/20 pl-4">{DISCIPLINA.adaptacao}</p>
                        <p data-testid="disciplina-objetivo" className="font-serif text-[21px] leading-[1.45] border-l-2 border-[#C7A659] pl-6">{DISCIPLINA.objetivo}</p>
                    </Reveal>
                    <Reveal delay={180}>
                        <p className="font-sans text-[10.5px] uppercase tracking-[0.28em] text-[#8a6a22]">Perguntas da disciplina</p>
                        <ul data-testid="disciplina-perguntas" className="mt-3 flex flex-col">
                            {DISCIPLINA.perguntas.map((q) => (
                                <li key={q} className="py-4 border-t border-[#0E2018]/15 font-serif italic text-[18px] leading-[1.45]">{q}</li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
                <Reveal className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8"><MapaAprendizagem /></div>
                    <div className="lg:col-span-4 flex flex-col gap-5">
                        <p data-testid="mapa-legenda" className="font-serif text-[19px] leading-[1.5]">{DISCIPLINA.mapaLegenda}</p>
                        <p data-testid="disciplina-principio" className="font-sans font-light text-[13.5px] leading-[1.8] text-[#2d3a31]">{DISCIPLINA.principio}</p>
                        <p data-testid="mapa-nota" className="font-sans text-[11px] leading-[1.6] text-[#5b6658]">{DISCIPLINA.mapaNota}</p>
                    </div>
                </Reveal>
                <Reveal className="mt-16">
                    <H2 light className="!text-[26px] md:!text-[32px]">Eixos a serem incorporados progressivamente</H2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#0E2018]/10 border border-[#0E2018]/10">
                        {DISCIPLINA.eixos.map((e, i) => (
                            <div key={e.t} data-testid={`disciplina-eixo-${i}`} className="bg-[#F2EFE5] p-7 md:p-8">
                                <h3 className="font-serif font-light text-[21px] leading-[1.3]">{e.t}</h3>
                                <p className="mt-3 font-sans font-light text-[13.5px] leading-[1.7] text-[#2d3a31]">{e.d}</p>
                            </div>
                        ))}
                    </div>
                    <div data-testid="disciplina-trabalho" className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 border-t-2 border-[#C7A659] pt-8">
                        <div className="lg:col-span-5">
                            <h3 className="font-serif font-light text-[22px] md:text-[24px] leading-[1.3]">{DISCIPLINA.trabalho.t}</h3>
                            <p className="mt-3 font-sans font-light text-[14px] leading-[1.8] text-[#2d3a31]">{DISCIPLINA.trabalho.d}</p>
                        </div>
                        <ul className="lg:col-span-7 flex flex-col">
                            {DISCIPLINA.trabalho.q.map((q) => (
                                <li key={q} className="py-3.5 border-t border-[#0E2018]/15 font-serif italic text-[17px] leading-[1.45]">{q}</li>
                            ))}
                        </ul>
                    </div>
                </Reveal>
                <QuestoesTransversais data={DISCIPLINA.transversais} />
                <Reveal className="mt-16">
                    <div data-testid="disciplina-individuacao" className="max-w-3xl">
                        <p className="font-sans text-[10.5px] uppercase tracking-[0.28em] text-[#8a6a22]">Lente · individuação</p>
                        <p className="mt-3 font-sans font-light text-[14px] leading-[1.8] text-[#2d3a31]">{ind.d}</p>
                        <p className="mt-3 font-serif text-[17px] leading-[1.55]">{DISCIPLINA.individuacaoNota}</p>
                        <p data-testid="individuacao-dialogo" className="mt-3 font-sans text-[12.5px] leading-[1.7] text-[#5b6658]">{DISCIPLINA.individuacaoDialogo}</p>
                    </div>
                </Reveal>
            </Container>
        </section>
    );
};

const Formatos = () => (
    <section data-section-id="formatos" className="py-20 md:py-28">
        <Container>
            <Reveal className="max-w-3xl">
                <Eyebrow>Materiais e formato</Eyebrow>
                <H2>Estrutura preparada para o ensino a distância.</H2>
            </Reveal>
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#1B3025] border border-[#1B3025]">
                {DISCIPLINA.formatos.map((f) => (
                    <div key={f.t} data-testid={`docencia-formato-${f.t.split(" ")[0].toLowerCase()}`} className="bg-[#0E2018] p-7">
                        <p className="font-serif font-light text-[20px] text-[#F2EFE5]">{f.t}</p>
                        <p className="mt-3 font-sans text-[10.5px] uppercase tracking-[0.22em] text-[#C7A659]">{f.s}</p>
                    </div>
                ))}
            </div>
            <div className="mt-12">
                <ArrowLink to="/contato" testId="docencia-link-contato">Convites e cooperação</ArrowLink>
            </div>
        </Container>
    </section>
);

const DocenciaPage = () => {
    const { hash } = useLocation();
    useEffect(() => {
        if (hash) setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 150);
    }, [hash]);

    return (
        <PageShell path="/docencia" title="Docência" sections={["header", "abordagem", "disciplina", "formatos"]}>
            <PageHeader testId="docencia-page-header" eyebrow={DOCENCIA.eyebrow} title={DOCENCIA.title} lead={DOCENCIA.lead} lead2={DOCENCIA.lead2} img={IMG.docencia} alt="Notebook exibindo uma videoaula, ao lado de um caderno aberto" />
            <Abordagem />
            <Disciplina />
            <Formatos />
        </PageShell>
    );
};

export default DocenciaPage;
